import { defineStore } from 'pinia';
import { cloneDeep, findIndex, find } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import { set, unset } from '@/helpers';
import NestedSet from '@/nested-set-lib';
import CategoryApi from '@/extensions/shop/store/api/category';

const defaultForm = {
    categories: []
};

const defaultRules = {
    categories: []
};

const defaultUi = {
    expandAll: true,
    form: {
        initialized: false
    }
};

export const useShopCategoriesStore = defineStore('shopCategories', {
    state: () => ({
        loading: true,
        options: {
            category: {
                loading: false,
                items: []
            }
        },
        validation: {
            usedKeys: []
        },
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized
    },

    actions: {
        setState({ path, value }) {
            set(this, path, value);
        },

        unsetState(path) {
            unset(this, path);
        },

        updateLoading(status) {
            Vue.set(this, 'loading', status);
        },

        insertCategories(categories) {
            const validatedItems = NestedSet.validate(cloneDeep(categories));
            categories.map(el => {
                if (validatedItems.invalid.IDs.includes(el.id)) {
                    el._lft = 0;
                    el._rgt = 0;
                }
            });

            let validItems = categories.filter(el => validatedItems.valid.IDs.includes(el.id));
            const invalidItems = categories.filter(el => validatedItems.invalid.IDs.includes(el.id));

            let orphans = [];
            if (invalidItems.length) {
                categories = categories.sort((a, b) => b._rgt - a._rgt);

                let lft = categories[0]._rgt + 1;
                orphans = [
                    {
                        id: '__orphans',
                        key: '__orphans',
                        name: 'Orphans',
                        _lft: lft,
                        _rgt: undefined
                    }
                ];

                lft++;

                invalidItems.forEach(el => {
                    const orphan = {
                        id: el.id,
                        key: el.key,
                        name: el.name,
                        _lft: lft,
                        _rgt: lft + 1
                    };

                    orphans.push(orphan);
                    lft = lft + 2;
                });

                orphans[0]._rgt = lft;
            }

            Vue.set(this.form, 'categories', NestedSet.fix([...validItems, ...orphans]) || []);
        },

        insertCategory(data) {
            const i = findIndex(this.form.categories, item => item.id === data.id);
            if (i >= 0) {
                Vue.set(this.form.categories, i, data);
            }
        },

        updateCategoryOptionsLoading(value) {
            Vue.set(this.options.category, 'loading', value);
        },

        insertCategoryOptions(categories) {
            Vue.set(this.options.category, 'items', categories);
        },

        expandAll() {
            this.ui.expandAll = true;
        },

        collapseAll() {
            this.ui.expandAll = false;
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        markFormCategoryAsHidden(item) {
            const index = findIndex(this.form.categories, { id: item.id });
            if (index >= 0) Vue.set(this.form.categories[index], 'hidden', true);
        },

        unmarkFormCategoryAsHidden(item) {
            const index = findIndex(this.form.categories, { id: item.id });
            if (index >= 0) Vue.set(this.form.categories[index], 'hidden', false);
        },

        markFormCategoryAsDeleted(item) {
            const index = findIndex(this.form.categories, { id: item.id });
            if (index >= 0) Vue.set(this.form.categories[index], 'deleted', true);
        },

        unmarkFormCategoryAsDeleted(item) {
            const index = findIndex(this.form.categories, { id: item.id });
            if (index >= 0) Vue.set(this.form.categories[index], 'deleted', false);
        },

        addFormCategory({ data, target, position }) {
            target = find(this.form.categories, { id: target });

            let items = cloneDeep(this.form.categories);
            let item = cloneDeep(data);

            if (!item.id) item.id = uniqid('new:');

            if (target && position) {
                switch (position) {
                    case 'after':
                        items = NestedSet.addAfter(items, target, item);
                        break;
                    case 'into':
                        items = NestedSet.appendTo(items, target, item);
                        break;
                }
            } else {
                items = NestedSet.append(items, item);
            }

            Vue.set(this.form, 'categories', items);
        },

        moveFormCategory({ item, target, position }) {
            let categories = cloneDeep(this.form.categories);
            item = cloneDeep(item);

            switch (position) {
                case 'before':
                    categories = NestedSet.moveBefore(categories, target, item);
                    break;
                case 'after':
                    categories = NestedSet.moveAfter(categories, target, item);
                    break;
                case 'inner':
                    categories = NestedSet.moveInto(categories, target, item);
                    break;
            }

            Vue.set(this.form, 'categories', categories);
        },

        setFormCategoryName({ i, value }) {
            Vue.set(this.form.categories[i], 'name', value);
        },

        setFormCategoryKey({ i, value }) {
            Vue.set(this.form.categories[i], 'key', value);
        },

        addUsedCategoryKeysToState(keys) {
            Vue.set(this.validation, 'usedKeys', [...keys]);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        // Async actions
        fetchCategories(shopID) {
            return CategoryApi.fetchList(shopID).then(categories => {
                this.insertCategories(categories);
            });
        },

        fetchCategory(id) {
            return CategoryApi.fetchItem(id).then(item => {
                this.insertCategory(cloneDeep(item));
                this.resetRules();

                return item;
            });
        },

        save({ categories, shop }) {
            categories = cloneDeep(categories);

            categories.forEach((category, i) => {
                if (category.deleted) NestedSet.deleteItem(categories, category);
                if (category.id === '__orphans') {
                    const children = NestedSet.getChildren(categories, category);
                    children.forEach(el => {
                        delete el._lft;
                        delete el._rgt;
                    });
                    categories.splice(i, 1);
                }
            });
            return CategoryApi.saveAll(categories, shop);
        },

        fetchCategoryOptions(shopID) {
            this.updateCategoryOptionsLoading(true);
            return CategoryApi.fetchList(shopID)
                .then(categories => {
                    this.updateCategoryOptionsLoading(false);
                    this.insertCategoryOptions(categories);
                })
                .catch(err => {
                    this.updateCategoryOptionsLoading(false);
                    throw err;
                });
        },

        reset() {
            this.insertCategories(cloneDeep(defaultForm.categories));
        },

        // Aliases for backward compatibility
        set(payload) {
            return this.setState(payload);
        },

        addCategory(payload) {
            return this.addFormCategory(payload);
        },

        moveCategory(payload) {
            return this.moveFormCategory(payload);
        },

        markCategoryAsHidden(data) {
            return this.markFormCategoryAsHidden(data);
        },

        unmarkCategoryAsHidden(data) {
            return this.unmarkFormCategoryAsHidden(data);
        },

        markCategoryAsDeleted(data) {
            return this.markFormCategoryAsDeleted(data);
        },

        unmarkCategoryAsDeleted(data) {
            return this.unmarkFormCategoryAsDeleted(data);
        },

        setFormName(payload) {
            return this.setFormCategoryName(payload);
        },

        setFormKey(payload) {
            return this.setFormCategoryKey(payload);
        }
    }
});
